// src/pages/canvasConfetti.js
import confetti from 'canvas-confetti';
import { useEffect } from 'react';
import { createRoot } from 'react-dom/client';

const CoinPopup = ({ earned, total }) => (
  <div style={{
    position: 'fixed',
    top: '40%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 999999999,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px',
    pointerEvents: 'none',
    animation: 'medPopAnim 3.2s ease-out forwards',
    fontFamily: '"Plus Jakarta Sans", sans-serif',
  }}>
    <div style={{
      width: '72px', height: '72px', borderRadius: '50%',
      background: 'linear-gradient(145deg, #E8F5F0, #C6EAE0)',
      border: '2.5px solid #34A87C', display: 'flex',
      alignItems: 'center', justifyContent: 'center',
      fontSize: '32px',
      boxShadow: '0 0 0 8px rgba(52,168,124,0.12), 0 8px 24px rgba(52,168,124,0.25)',
      animation: 'ringPulse 0.6s ease-out',
    }}>🏥</div>

    <div style={{
      background: 'linear-gradient(135deg, #34A87C 0%, #1E8A61 100%)',
      color: '#fff', fontWeight: 700, fontSize: '20px',
      letterSpacing: '-0.3px', padding: '8px 22px', borderRadius: '999px',
      boxShadow: '0 6px 20px rgba(30,138,97,0.4), inset 0 1px 0 rgba(255,255,255,0.2)',
      whiteSpace: 'nowrap',
    }}>
      +{earned} Health Points
    </div>

    <div style={{
      background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(12px)',
      color: '#2D6A4F', fontSize: '13px', fontWeight: 500,
      padding: '5px 16px', borderRadius: '999px',
      border: '1px solid rgba(52,168,124,0.25)',
      boxShadow: '0 2px 12px rgba(0,0,0,0.06)', letterSpacing: '0.01em',
    }}>
      Great job! Total: {total} pts
    </div>

    <div style={{
      fontSize: '11px', color: 'rgba(45,106,79,0.7)',
      fontWeight: 400, letterSpacing: '0.03em',
    }}>
      Keep going · You're doing great 💪
    </div>
  </div>
);

const injectStyles = () => {
  if (document.getElementById('med-coin-styles')) return;
  const style = document.createElement('style');
  style.id = 'med-coin-styles';
  style.innerHTML = `
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;700&display=swap');
    @keyframes medPopAnim {
      0%   { opacity: 0; transform: translate(-50%, -40%) scale(0.7); }
      12%  { opacity: 1; transform: translate(-50%, -52%) scale(1.05); }
      22%  { transform: translate(-50%, -50%) scale(1); }
      65%  { opacity: 1; transform: translate(-50%, -58%); }
      100% { opacity: 0; transform: translate(-50%, -72%); }
    }
    @keyframes ringPulse {
      0%   { transform: scale(0.4); opacity: 0; }
      60%  { transform: scale(1.1); opacity: 1; }
      100% { transform: scale(1); }
    }
  `;
  document.head.appendChild(style);
};

const showCoinPopup = (earned, total) => {
  injectStyles();
  const el = document.createElement('div');
  document.body.appendChild(el);
  const root = createRoot(el);
  root.render(<CoinPopup earned={earned} total={total} />);
  setTimeout(() => {
    root.unmount();
    el.remove();
  }, 3300);
};

// ── fire confetti + popup ──
const fireCoins = (earned, total) => {
  if (earned > 0) {
    const heartShape = confetti.shapeFromText({ text: '💚', scalar: 3 });
    const starShape  = confetti.shapeFromText({ text: '✨', scalar: 3 });

    confetti({
      particleCount: Math.min(earned, 40), spread: 120, ticks: 220,
      gravity: 0.5, origin: { x: 0.5, y: 0.1 },
      shapes: [heartShape, starShape], zIndex: 999999999,
      startVelocity: 22, scalar: 3,
    });
    confetti({
      particleCount: 15, angle: 60, spread: 55,
      origin: { x: 0, y: 0.6 },
      colors: ['#34A87C', '#6ECFAA', '#B7EDD8', '#ffffff'],
      ticks: 180, gravity: 0.6, zIndex: 999999999, startVelocity: 35,
    });
    confetti({
      particleCount: 15, angle: 120, spread: 55,
      origin: { x: 1, y: 0.6 },
      colors: ['#34A87C', '#6ECFAA', '#B7EDD8', '#ffffff'],
      ticks: 180, gravity: 0.6, zIndex: 999999999, startVelocity: 35,
    });
  }
  showCoinPopup(earned, total);
};

// ── Main component ──
const CoinPop = () => {
  useEffect(() => {

    // ── check localStorage / sessionStorage on mount ──
    const stored = localStorage.getItem('coinSummary') || sessionStorage.getItem('coinSummary');
    if (stored) {
      try {
        const { earned, total } = JSON.parse(stored);
        if (earned || total) {
          setTimeout(() => fireCoins(earned, total), 400); // wait for page paint
        }
      } catch (e) {
        console.error('CoinPop: failed to parse stored coins', e);
      } finally {
        // ── clear both storages after consuming ──
        localStorage.removeItem('coinSummary');
        sessionStorage.removeItem('coinSummary');
      }
    }

    // ── live event listener ──
    const handleTrigger = (event) => {
      const { earned, total } = event.detail;
      if (!earned && !total) return;
      fireCoins(earned, total);
    };

    window.addEventListener('show-coins', handleTrigger);
    return () => window.removeEventListener('show-coins', handleTrigger);
  }, []);

  return null;
};

// ── triggerCoins — saves to storage + dispatches event ──
export const triggerCoins = (earned, total) => {
  if (!earned && !total) return;

  const coinData = JSON.stringify({ earned, total });

  // ── save to both storages so page reload still works ──
  localStorage.setItem('coinSummary', coinData);
  sessionStorage.setItem('coinSummary', coinData);

  // ── also dispatch event for same-page triggers ──
  window.dispatchEvent(new CustomEvent('show-coins', {
    detail: { earned, total },
  }));
};

export default CoinPop;